import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Bell, ChevronRight, Download, FileText, LayoutGrid, LogOut, Moon, Pencil, ShieldCheck, Smartphone, Sun, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useToast } from "@/lib/toast";
import { browserNotificationsSupported, notificationsEnabled, requestNotificationPermission, setNotificationsEnabled } from "@/lib/desktopNotify";
import { installApp, isStandalone } from "@/lib/pwa";
import { Avatar, Button, Card, Overline, Toggle } from "@/components/app/Primitives";

function Row({ icon: Icon, danger, title, sub, onClick, testID }) {
  return (
    <button onClick={onClick} data-testid={testID} className="group flex w-full items-center gap-3 py-2.5 text-left">
      <span className={`flex h-9 w-9 items-center justify-center rounded-[10px] ${danger ? "bg-a-danger/15 text-a-danger" : "bg-a-dim text-a-primary"}`}><Icon size={18} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-bold text-a-text group-hover:text-a-primary">{title}</p>
        <p className="truncate text-xs text-a-muted">{sub}</p>
      </div>
      <ChevronRight size={18} className="text-a-muted transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

export default function Profile() {
  const { user, workspaces, activeWorkspace, signOut } = useAuth();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const themes = [{ key: "light", label: "Light", icon: Sun }, { key: "dark", label: "Dark", icon: Moon }, { key: "system", label: "System", icon: Smartphone }];
  const [notifyOn, setNotifyOn] = useState(() => notificationsEnabled() && typeof Notification !== "undefined" && Notification.permission === "granted");
  const Div = () => <div className="h-px bg-a-border" />;

  const install = async () => {
    const r = await installApp();
    if (r === "installed") toast.show("Assignoo is already installed", "info");
    else if (r === "ios") toast.show("On iPhone: Share → Add to Home Screen", "info");
    else if (r === "unavailable") toast.show("Install isn't available here — use Chrome, Edge, or Safari's Add to Home Screen.", "info");
  };
  const toggleNotify = async (next) => {
    if (next) {
      if (!browserNotificationsSupported()) return toast.show("This browser doesn't support notifications", "error");
      const p = await requestNotificationPermission();
      if (p !== "granted") return toast.show("Notifications are blocked for this site. Allow them in the browser's site settings.", "error");
      setNotificationsEnabled(true);
      setNotifyOn(true);
      toast.show("Browser alerts on — you'll get notified about new assignments, approvals and comments while Assignoo is open.", "success");
    } else {
      setNotificationsEnabled(false);
      setNotifyOn(false);
      toast.show("Browser alerts off", "info");
    }
  };

  return (
    <div className="page-in space-y-4 px-4 pt-4 md:pt-8" data-testid="profile-page">
      <div className="pt-2"><Overline>Profile</Overline><h1 className="text-[28px] font-extrabold tracking-tight text-a-text">Your account</h1></div>
      <Card>
        <div className="flex items-center gap-3.5">
          <Avatar src={user?.avatar_base64} name={user?.full_name} size={64} />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="truncate text-lg font-extrabold text-a-text" data-testid="profile-name">{user?.full_name}</p>
            <p className="truncate text-a-muted">{user?.email}</p>
            {user?.designation ? <p className="text-xs text-a-muted">{user.designation}</p> : null}
          </div>
          <button onClick={() => navigate("/profile/edit")} data-testid="profile-edit-button" className="flex items-center gap-1 rounded-full border border-a-border bg-a-surface px-3 py-1.5 text-xs font-bold text-a-primary hover:border-a-primary"><Pencil size={14} />Edit</button>
        </div>
      </Card>
      <Card className="!py-1.5">
        <Row testID="profile-workspaces" icon={LayoutGrid} title="Workspace" sub={`${activeWorkspace?.name || ""} · ${workspaces.length} available`} onClick={() => navigate("/workspaces")} />
        <Div />
        <Row testID="profile-security" icon={ShieldCheck} title="Security & privacy" sub="Verify email/mobile · sessions · privacy · audit" onClick={() => navigate("/security")} />
        <Div />
        <Row testID="profile-reports" icon={BarChart3} title="Reports" sub="Team productivity & completion" onClick={() => navigate("/reports")} />
        <Div />
        <Row testID="profile-privacy" icon={FileText} title="Privacy Policy" sub="How we handle your data · account deletion" onClick={() => navigate("/privacy-policy")} />
        <Div />
        <Row testID="profile-delete-account" icon={Trash2} danger title="Delete account" sub="Permanently remove your account & personal data" onClick={() => navigate("/delete-account")} />
      </Card>
      {user?.platform_admin ? (
        <Card className="!py-1.5">
          <Row testID="profile-admin" icon={ShieldCheck} danger title="Admin Console" sub="Owner-only · manage all users, exports, deactivations" onClick={() => navigate("/admin")} />
        </Card>
      ) : null}
      <Card>
        <Overline className="mb-2.5">Appearance</Overline>
        <div className="flex gap-2">
          {themes.map((t) => {
            const active = mode === t.key;
            return (
              <button key={t.key} data-testid={`theme-${t.key}`} onClick={() => { setMode(t.key); toast.show(`Theme: ${t.label}`, "info"); }}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 font-bold transition-colors ${active ? "border-a-primary bg-a-primary text-white" : "border-a-border bg-a-surface text-a-text hover:border-a-primary/50"}`}>
                <t.icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
      </Card>
      <Card className="space-y-1">
        <Overline className="mb-2">App</Overline>
        <div className="flex items-center gap-3 py-1">
          <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-a-dim text-a-primary"><Download size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-a-text">Install Assignoo</p>
            <p className="truncate text-xs text-a-muted">{isStandalone() ? "Installed — running as an app" : "Add to home screen or desktop"}</p>
          </div>
          {!isStandalone() ? (
            <button onClick={install} data-testid="profile-install" className="rounded-lg bg-a-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110">Install</button>
          ) : null}
        </div>
        <div className="h-px bg-a-border" />
        <div className="flex items-center gap-3 py-1">
          <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-a-dim text-a-primary"><Bell size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-a-text">Browser alerts</p>
            <p className="truncate text-xs text-a-muted">Desktop notifications for new tasks, approvals &amp; comments</p>
          </div>
          <Toggle testID="profile-notifications" value={notifyOn} onChange={toggleNotify} />
        </div>
      </Card>
      <Button testID="profile-signout" label="Sign out" variant="ghost" icon={<LogOut size={16} />} onPress={async () => { await signOut(); navigate("/login", { replace: true }); }} />
    </div>
  );
}
